import { useEffect, useState, useMemo } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from '../AuthContext';
import { useLanguage } from '../LanguageContext';
import translations from '../translations';
import RecipeCard from './RecipeCard';
import { mergeRecipeTitles } from '../utils/fetchTranslatedRecipes';
import './CategoryRecipeList.css'; // reuse same grid + filter bar styles

function CollectionRecipeList({ collection, onSelectRecipe, onBack }) {
  const { language } = useLanguage();
  const t = translations[language];
  const { user, isAdmin, isMember } = useAuth();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);

  const [sort, setSort] = useState('newest');
  const [diffFilter, setDiffFilter] = useState('');
  const [maxTime, setMaxTime] = useState(null);
  const [dietFilters, setDietFilters] = useState(new Set());

  function toggleMaxTime(val) { setMaxTime(prev => prev === val ? null : val); }
  function toggleDiet(key) {
    setDietFilters(prev => { const next = new Set(prev); if (next.has(key)) next.delete(key); else next.add(key); return next; });
  }
  function clearAllFilters() { setMaxTime(null); setDietFilters(new Set()); }
  const hasActiveFilters = maxTime !== null || dietFilters.size > 0;

  useEffect(() => {
    async function fetch() {
      const { data: links } = await supabase
        .from('collection_recipes')
        .select('recipe_id')
        .eq('collection_id', collection.id);

      if (!links || links.length === 0) { setLoading(false); return; }

      const ids = links.map(l => l.recipe_id);
      const { data: recs } = await supabase
        .from('recipes')
        .select('*')
        .in('id', ids);

      if (recs) {
        const merged = await mergeRecipeTitles(recs, language);
        setRecipes(merged);
      }
      setLoading(false);
    }
    fetch();
  }, [collection.id, language]);

  async function handleToggleFavourite(id, newValue) {
    setRecipes(prev => prev.map(r => r.id === id ? { ...r, is_favourite: newValue } : r));
    if (isMember && user) {
      if (newValue) await supabase.from('user_favourites').insert({ user_id: user.id, recipe_id: id });
      else await supabase.from('user_favourites').delete().eq('user_id', user.id).eq('recipe_id', id);
    } else {
      await supabase.from('recipes').update({ is_favourite: newValue }).eq('id', id);
    }
  }

  const displayed = useMemo(() => {
    let list = [...recipes];
    if (diffFilter) list = list.filter(r => r.difficulty === diffFilter);
    if (maxTime === 30) list = list.filter(r => (r.prep_time_mins||0)+(r.cook_time_mins||0) < 30);
    if (maxTime === 60) list = list.filter(r => (r.prep_time_mins||0)+(r.cook_time_mins||0) >= 60);
    if (dietFilters.size > 0) {
      list = list.filter(r => {
        const tags = (r.dietary_tags || []).map(t => t.toLowerCase().replace(/\s+/g, '-'));
        if (dietFilters.has('vegetarian') && !tags.some(t => t.includes('vegetarian'))) return false;
        if (dietFilters.has('low-carb') && !tags.some(t => t.includes('low') && t.includes('carb'))) return false;
        return true;
      });
    }
    switch (sort) {
      case 'az':       list.sort((a, b) => a.title.localeCompare(b.title)); break;
      case 'za':       list.sort((a, b) => b.title.localeCompare(a.title)); break;
      case 'quickest': list.sort((a, b) => ((a.prep_time_mins||0)+(a.cook_time_mins||0)) - ((b.prep_time_mins||0)+(b.cook_time_mins||0))); break;
      default:         list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)); break;
    }
    return list;
  }, [recipes, diffFilter, maxTime, dietFilters, sort]);

  return (
    <main className="category-recipe-list">
      <div className="category-recipe-list__header">
        <button className="category-recipe-list__back" onClick={onBack}>
          ← {t.collectionsTitle}
        </button>
        <h2 className="category-recipe-list__title">{collection.name}</h2>
      </div>

      <div className="recipe-filter-bar">
        <div className="recipe-filter-bar__group">
          <span className="recipe-filter-bar__label">{t.sortLabel}</span>
          {[
            { value: 'newest',   label: t.sortNewest },
            { value: 'az',       label: t.sortAZ },
            { value: 'za',       label: t.sortZA },
            { value: 'quickest', label: t.sortQuickest },
          ].map(opt => (
            <button
              key={opt.value}
              className={`recipe-filter-bar__btn${sort === opt.value ? ' recipe-filter-bar__btn--active' : ''}`}
              onClick={() => setSort(opt.value)}
            >{opt.label}</button>
          ))}
        </div>
        <div className="recipe-filter-bar__group">
          <button
            className={`recipe-filter-bar__btn${maxTime === 30 ? ' recipe-filter-bar__btn--active' : ''}`}
            onClick={() => toggleMaxTime(30)}
          >{t.filterUnder30}</button>
          <button
            className={`recipe-filter-bar__btn${maxTime === 60 ? ' recipe-filter-bar__btn--active' : ''}`}
            onClick={() => toggleMaxTime(60)}
          >{t.filter60Plus}</button>
          <button
            className={`recipe-filter-bar__btn${dietFilters.has('vegetarian') ? ' recipe-filter-bar__btn--active' : ''}`}
            onClick={() => toggleDiet('vegetarian')}
          >{t.filterVegetarian}</button>
          <button
            className={`recipe-filter-bar__btn${dietFilters.has('low-carb') ? ' recipe-filter-bar__btn--active' : ''}`}
            onClick={() => toggleDiet('low-carb')}
          >{t.filterLowCarb}</button>
        </div>
      </div>

      {loading ? (
        <p style={{ color: '#999', fontStyle: 'italic' }}>{t.loading}</p>
      ) : displayed.length === 0 ? (
        <div className="category-recipe-list__empty">
          <p>{hasActiveFilters ? t.noRecipesMatchFilters : t.collectionEmpty}</p>
          {hasActiveFilters && (
            <button className="recipe-filter-bar__btn" onClick={clearAllFilters}>{t.clearFilters}</button>
          )}
        </div>
      ) : (
        <div className="category-recipe-list__grid">
          {displayed.map(recipe => (
            <RecipeCard
              key={recipe.id}
              recipe={recipe}
              onClick={() => onSelectRecipe(recipe.id)}
              onToggleFavourite={handleToggleFavourite}
              isAdmin={isAdmin || isMember}
              showDraft={isAdmin}
            />
          ))}
        </div>
      )}
    </main>
  );
}

export default CollectionRecipeList;
